import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin");
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center bg-brand-bg px-5" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md glass rounded-3xl p-8 sm:p-10"
      >
        <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-3">Admin</p>
        <h1 className="font-display text-4xl mb-8">Deli La Bendición</h1>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-muted">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-2 w-full rounded-xl bg-brand-surface border border-brand-line px-4 py-3 text-brand-text outline-none focus:border-brand-gold transition-colors"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-muted">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="mt-2 w-full rounded-xl bg-brand-surface border border-brand-line px-4 py-3 text-brand-text outline-none focus:border-brand-gold transition-colors"
              required
            />
          </div>

          {error && <p className="text-sm text-brand-primary" data-testid="login-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full rounded-xl bg-brand-primary hover:bg-brand-primaryHover py-3.5 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
